import { useRef, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { 
  Alert,
  Box, 
  Container, 
  FormControl, 
  IconButton, 
  InputAdornment, 
  InputLabel, 
  List, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText, 
  OutlinedInput,
  Snackbar,
  Typography
} from '@mui/material'
import LoadingButton from '@mui/lab/LoadingButton'
import { 
  Visibility, 
  VisibilityOff,
  Close,
  PersonAdd,
  Google,
  Help
} from '@mui/icons-material'

import src from '../../assets/ssk.png'
import Loading from './loading'
import { 
  useSignInEmail, 
  useSignInGoogle 
} from '../hooks/auth'

export default function Login() {
  const [signInEmail, loadingEmail, errorEmail, clearEmailError] = useSignInEmail()
  const [signInGoogle, loadingGoogle, errorGoogle, clearGoogleError] = useSignInGoogle()
  const [email, setEmail] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [showPasswordButton, setShowPasswordButton] = useState(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const passwordRef = useRef<HTMLInputElement>(null)

  const handleShowPassword = () => setShowPassword(!showPassword)

  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) =>
    e.preventDefault()

  const handleSignInEmail = async () => {
    setShowPassword(false)
    signInEmail(email, password)
  }

  if (loadingGoogle) return <Loading />

  return (
    <Container maxWidth="xs">
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        marginY: 4,
      }}>
        <Box sx={{
          width: 128,
          height: 128
        }}>
          <img
          style={{ maxWidth: '100%' }}
            src={src}
            alt="Sunderby Sportklubb"
          />
        </Box>
      </Box>
      
      <FormControl fullWidth variant="outlined">
        <InputLabel htmlFor="email" error={!!errorEmail?.email}>E-post</InputLabel>
        <OutlinedInput
          id="email"
          label="E-post"
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyUp={e => e.key === 'Enter' && passwordRef.current && passwordRef.current.focus()}
          disabled={loadingEmail}
          error={!!errorEmail?.email}
        />
      </FormControl>

      <FormControl fullWidth sx={{ mt: 2 }} variant="outlined">
        <InputLabel htmlFor="password" error={!!errorEmail?.password}>Lösenord</InputLabel>
        <OutlinedInput
          ref={passwordRef}
          id="password"
          label="Lösenord"
          value={password}
          type={showPassword ? 'text' : 'password'}
          onFocus={() => setShowPasswordButton(true)}
          onBlur={() => setShowPasswordButton(false)}
          onChange={e => setPassword(e.target.value)}
          onKeyUp={e => e.key === 'Enter' && handleSignInEmail()}
          disabled={loadingEmail}
          error={!!errorEmail?.password}
          endAdornment={showPasswordButton &&
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleShowPassword}
                onMouseDown={handleMouseDown}
                disabled={loadingEmail}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
      </FormControl>

      <Box mt={2}>
        <LoadingButton
          fullWidth
          size="large"
          loading={loadingEmail}
          variant="contained"
          disabled={loadingEmail}
          onClick={handleSignInEmail}
        >
          Logga in
        </LoadingButton>
      </Box>

      <Box mt={2}>
        <List disablePadding>
          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/forgot">
              <ListItemIcon>
                <Help />
              </ListItemIcon>
              <ListItemText primary="Glömt lösenordet?" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton component={RouterLink} to="/register">
              <ListItemIcon>
                <PersonAdd />
              </ListItemIcon>
              <ListItemText primary="Registrera konto" />
            </ListItemButton>
          </ListItem>

          <ListItem disablePadding>
            <ListItemButton
              disabled={loadingGoogle}
              onClick={signInGoogle}
            >
              <ListItemIcon>
                <Google />
              </ListItemIcon>
              <ListItemText primary="Logga in med Google" />
            </ListItemButton>
          </ListItem>
        </List>
      </Box>

      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        marginY: 3,
        textAlign: 'center',
      }}>
        <Typography variant="body2" color="text.secondary">
          Appen är byggd av Najsit Consulting AB.
        </Typography>
      </Box>

      <Snackbar 
        open={!!errorEmail || !!errorGoogle} 
        autoHideDuration={6000} 
        onClose={() => {
          clearEmailError()
          clearGoogleError()
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          severity="error"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={() => {
                clearEmailError()
                clearGoogleError()
              }}
            >
              <Close />
            </IconButton>
          }
        >
          {errorEmail?.email}
          {errorEmail?.password}
          {errorEmail?.other}
          {errorGoogle?.other}
        </Alert>
      </Snackbar>
    </Container>
  )
}
